import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import { api, fetchApiBlob } from "../../../lib/api";
import { getApiErrorMessage } from "../../../lib/apiError";

type Branding = {
  brand_color_1: string;
  brand_color_2: string;
  logo_url: string | null;
};
type Changes = Partial<Pick<Branding, "brand_color_1" | "brand_color_2">> & {
  logo?: File;
  remove_logo?: boolean;
};
const defaults: Branding = {
  brand_color_1: "#7300FF",
  brand_color_2: "#1B1B28",
  logo_url: null,
};

export function useTrialBranding(leadId: string, enabled: boolean) {
  const client = useQueryClient();
  const key = ["admin", "trial-branding", leadId];
  const endpoint = `/api/admin/crm/leads/${leadId}/trial-branding/`;
  const [changes, setChanges] = useState<Changes | null>(null);
  const [error, setError] = useState("");
  const query = useQuery({
    queryKey: key,
    enabled,
    queryFn: async () => ({
      ...defaults,
      ...(await api.get<Branding>(endpoint)).data,
    }),
  });
  const save = useMutation({
    onMutate: async () => {
      await client.cancelQueries({ queryKey: key });
    },
    mutationFn: async (values: Changes) => {
      const data = new FormData();
      Object.entries(values).forEach(([field, value]) => {
        if (value !== undefined)
          data.append(field, value instanceof File ? value : String(value));
      });
      return (await api.patch<Branding>(endpoint, data)).data;
    },
    onSuccess: (data, values) => {
      client.setQueryData(key, data);
      setChanges((current) => (current === values ? null : current));
      setError("");
    },
    onError: (failure) =>
      setError(
        getApiErrorMessage(
          failure,
          "Branding could not be saved. Your selections are still here.",
        ),
      ),
  });
  const { mutate, isPending } = save;
  useEffect(() => {
    if (!changes || error || isPending || !query.data) return;
    const timer = window.setTimeout(() => mutate(changes), 500);
    return () => window.clearTimeout(timer);
  }, [changes, error, isPending, mutate, query.data]);
  const update = (values: Changes) => {
    if (isPending) return;
    setError("");
    setChanges((current) => ({ ...current, ...values }));
  };
  const chooseLogo = (file: File | undefined) => {
    if (!file) return;
    if (
      !["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setError("Choose a PNG, JPEG or WebP logo up to 5 MB.");
      return;
    }
    update({ logo: file, remove_logo: false });
  };
  return {
    colors: { ...defaults, ...query.data, ...changes },
    logo: changes?.remove_logo
      ? null
      : changes?.logo || query.data?.logo_url || null,
    ready: !!query.data && !query.isError && !error,
    unsaved: !!changes,
    saving: isPending || (!!changes && !error),
    loading: query.isPending,
    uploading: isPending,
    error: query.isError
      ? "Could not load saved branding. Retry before creating the account."
      : error,
    retry: () =>
      query.isError
        ? void query.refetch()
        : changes
          ? mutate(changes)
          : undefined,
    update,
    chooseLogo,
    useSaved:
      query.data && !query.isError
        ? () => {
            setChanges(null);
            setError("");
          }
        : undefined,
    removeLogo: () => {
      setError("");
      setChanges((current) => ({
        ...current,
        logo: undefined,
        remove_logo: true,
      }));
    },
  };
}

export function TrialBrandingEditor({
  branding,
  disabled,
}: {
  branding: ReturnType<typeof useTrialBranding>;
  disabled: boolean;
}) {
  const [preview, setPreview] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const { logo } = branding;
  useEffect(() => {
    let active = true;
    let url = "";
    setPreview("");
    setPreviewError(false);
    if (logo) {
      (logo instanceof File ? Promise.resolve(logo) : fetchApiBlob(logo))
        .then((blob) => {
          if (!active) return;
          url = URL.createObjectURL(blob);
          setPreview(url);
        })
        .catch(() => {
          if (active) setPreviewError(true);
        });
    }
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [logo]);
  const locked = disabled || branding.loading || branding.uploading;
  return (
    <Box component="details">
      <Typography
        component="summary"
        level="title-sm"
        sx={{ cursor: "pointer", display: "list-item", ml: 2 }}
      >
        Logo & brand colors (optional)
      </Typography>
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        <Typography level="body-sm">
          Saved automatically to this prospect. These will be applied when you
          create the account.
        </Typography>
        <Typography
          role="status"
          aria-live="polite"
          level="body-sm"
          color={branding.error ? "danger" : "success"}
        >
          {branding.loading
            ? "Loading saved branding…"
            : branding.error
              ? "Branding not saved"
              : branding.saving
                ? "Saving branding…"
                : "Branding saved"}
        </Typography>
        {branding.error && (
          <Alert color="danger">
            {branding.error}
            <Button
              size="sm"
              variant="outlined"
              disabled={branding.uploading}
              onClick={branding.retry}
            >
              Retry branding
            </Button>
            {branding.useSaved && (
              <Button
                size="sm"
                variant="plain"
                disabled={branding.uploading}
                onClick={branding.useSaved}
              >
                Use saved branding
              </Button>
            )}
          </Alert>
        )}
        {preview && (
          <Box
            component="img"
            src={preview}
            alt="Company logo preview"
            sx={{
              maxWidth: 220,
              height: 80,
              objectFit: "contain",
              alignSelf: "start",
              p: 1,
              bgcolor: "#fff",
              borderRadius: "sm",
            }}
          />
        )}
        {previewError && (
          <Typography level="body-sm">
            Logo saved, but its preview could not load.
          </Typography>
        )}
        <FormControl>
          <FormLabel htmlFor="trial-logo">Company logo</FormLabel>
          <input
            id="trial-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={locked}
            onChange={(event) => {
              branding.chooseLogo(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <FormHelperText>
            PNG, JPEG or WebP, up to 5 MB.{" "}
            {logo instanceof File
              ? logo.name
              : logo
                ? "Saved company logo"
                : ""}
          </FormHelperText>
        </FormControl>
        {logo && (
          <Button
            size="sm"
            variant="outlined"
            sx={{ alignSelf: "start" }}
            disabled={locked}
            onClick={branding.removeLogo}
          >
            Remove logo
          </Button>
        )}
        <Stack direction="row" spacing={2}>
          {(["brand_color_1", "brand_color_2"] as const).map((key, index) => (
            <FormControl key={key}>
              <FormLabel htmlFor={`trial-${key}`}>
                {index === 0 ? "Primary" : "Accent"} color
              </FormLabel>
              <Input
                slotProps={{ input: { id: `trial-${key}` } }}
                type="color"
                value={branding.colors[key]}
                disabled={locked}
                onChange={(event) =>
                  branding.update({ [key]: event.target.value })
                }
              />
            </FormControl>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
